<script setup lang="ts">
//#region imports
import { ref } from 'vue';
import type { EmitsToListeners } from '@/types/core';
import type { CrmTemplate } from '@/types/crm';
//#endregion

//#region types
type TemplateId = CrmTemplate['id'];

interface Props {
  templates?: CrmTemplate[];
  templateId?: TemplateId | null;
  isEdit?: boolean;
}

interface Emits {
  close: [];
  'select-template': [id: TemplateId | undefined];
}

export interface TemplateMailActionParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  templates: () => [],
  templateId: null,
  isEdit: false,
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const confirm = ref();
const isVisible = ref(false);
const selectedTemplateId = ref<TemplateId | undefined>(undefined);
//#endregion

//#region methods
function open() {
  selectedTemplateId.value = props.templateId || undefined;
  isVisible.value = true;
}

function close() {
  isVisible.value = false;
  emit('close');
}

function confirmSelectAction() {
  emit('select-template', selectedTemplateId.value);
  close();
}

function handleSelectTemplate() {
  if (props.templateId === selectedTemplateId.value && props.isEdit) {
    close();
    return;
  }

  if (props.isEdit) {
    confirm.value?.open();
  } else {
    confirmSelectAction();
  }
}
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <UiConfirm
    ref="confirm"
    :width="480"
    :action-name="$t('crm.buttons.change')"
    action-fa-icon="check"
    @save="confirmSelectAction"
  >
    <template v-slot:title>
      <div>
        {{ $t('crm.activities.actions.mail_template.confirm_title') }}
      </div>
    </template>

    <div class="body__wrap">
      <i class="success fas fa-exclamation-triangle t:mr-12" />
      <div class="body__msg">
        {{ $t('crm.activities.actions.mail_template.confirm_body') }}
      </div>
    </div>
  </UiConfirm>

  <Popup
    v-model:is-visible="isVisible"
    class="template-mail-action-popup"
    :title="$t('crm.activities.actions.action_popup.mail_template_title')"
    :width="1200"
    @close="close()"
  >
    <template v-slot:body>
      <div class="template-mail-action-popup__list">
        <div
          v-for="template in templates"
          :key="template.id"
          class="template-mail-action-popup__item"
        >
          <ElCard
            class="t:!border-none"
            :class="{
              'template-mail-action-popup__card_active': template.id === selectedTemplateId,
            }"
            :body-style="{ padding: '0px' }"
            shadow="never"
          >
            <img
              :src="template.preview_base64 || ''"
              class="template-mail-action-popup__image"
              :alt="template.name"
              @click="selectedTemplateId = template.id"
            >
            <div class="t:flex t:items-center t:justify-center t:py-14">
              <UiRadioButton
                v-model="selectedTemplateId"
                :value="template.id"
              >
                <UiTitle :font-size="16">
                  {{ template.name }}
                </UiTitle>
              </UiRadioButton>
            </div>
          </ElCard>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          class="t:mr-8"
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          :disabled="!selectedTemplateId"
          @click="handleSelectTemplate()"
        >
          {{ $t('crm.buttons.select_template') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.template-mail-action-popup {
  &__list {
  display: flex;
  flex-flow: row wrap;
  gap: 20px;
  }

  &__item {
    flex: 0 1 calc(20% - 16px);
  }

  &__image {
    width: 100%;
    height: 320px;
    display: block;
    cursor: pointer;
    border: 2px solid var(--border-default);
    box-sizing: border-box;
    border-radius: 8px;
  }

  &__card_active &__image {
    border-color: var(--border-focused);
  }
}
</style>
